/**
 * Structure which handles line data details
 * @typedef {Object} LineData
 * @property {Overlay} tooltip - A tooltip is bound to a line
 * @property {PolylineNode} node - The other Node of the line
 */

/**
 * Define a Polyline node with is eventual extensions
 * @typedef {Object} PolylineNode
 * @property {number[]} coordinates - Node coordinates [x, y]
 * @property {Map<string, LineData>} lines - Lines details link to the point
 * @property {string} hash - An hash representation of coordinates, used as key to optimized node search, O(1)
 * @property {number} index - Index of the node in polyline
 * */

import { getDistance } from 'ol/sphere';
import { toLonLat } from 'ol/proj';
import { round, azimuth, center } from '../helpers';
import Overlay from 'ol/Overlay';
import { LineString } from 'ol/geom';
import GeometryLayout from 'ol/geom/Geometry';
import { Control } from 'ol/control';
import { NAUTICAL_MILE_IN_METERS } from '../constantes';

export const EVENT_UPDATE_TOTAL_DISTANCE = 'update total distance';
export const EVENT_DISABLE_DRAWING = 'disable drawing';
export const EVENT_DRAW_STATUS = 'draw status';

/**
 * Hash a node coordinates
 * @param {[number, number]}coords
 * @return {string} The hashed coordinates
 */
export function hashNodeCoordinates(coords) {
  return `${coords[0]}_${coords[1]}`;
}

/** Returns distance in nautical mile (1852m) if it s greater than 1NM
 *  Otherwhhise returns distance in meter  **/
function formatDistance(distance) {
  const nmFrac = NAUTICAL_MILE_IN_METERS / 10
  let dist = 0;
  if (localStorage.getItem('measureUnit') === 'nautical') {
    dist = distance > nmFrac ? `${round(distance / NAUTICAL_MILE_IN_METERS, 2)} NM` : `${round(distance, 2)} m`;
  } else {
    dist = distance > 1000 ? `${round(distance / 1000, 2)} km` : `${round(distance, 2)} m`;
  }
  return dist;
}

/**
 * ===
 * Tooltip
 * ===
 */

/**
 * Calculates distance and azimuth between the two last geometry points and
 * format it to display.
 * @param {LineString} line The lineString geometry.
 * @returns {{distance: number, text: string}} The formatted distance and azimuth between the two last geometry
 *                   ready to set in a html element.
 */
export function getLineGeometryParameters(line) {
  const coords = line.getCoordinates();
  const secondToLast = toLonLat(coords[coords.length - 2]);
  const last = toLonLat(coords[coords.length - 1]);

  const distance = getDistance(secondToLast, last);
  const textDistance = formatDistance(distance);

  let azimuthValue = azimuth(secondToLast, last);
  azimuthValue = round(azimuthValue, 0);

  let text = `Distance: ${textDistance}`;
  if (!Number.isNaN(azimuthValue)) {
    text += `<br>Azimuth: ${azimuthValue}°`;
  }

  return { text, distance };
}

/**
 * Create a new tooltip handle by an Overlay structure
 * @param {boolean} transient - Whether this tooltip is a on way measure
 * @return {Overlay}
 */
export function createNewTooltip(transient = false) {
  // Create a new div element that handles the tooltip element over the map
  const element = document.createElement('div');
  const tooltipOverlay = new Overlay({
    stopEvent: false,
    element: element,
    offset: [0, -7],
    positioning: 'bottom-center'
  });
  setTooltipStyle(tooltipOverlay, transient);
  return tooltipOverlay;
}

export function createPolylineControl() {
  const template = document.createElement('template');
  template.innerHTML = `<div class="measure-route-control-container ol-control">
    <button id="measure-route-control-control">
      <i class="icon-lock-open-alt"></i>
    </button>
    <div class="measure-route-control-text">
       Total Distance: <span id="measure-route-total-distance"></span>
    </div>
</div>`;

  const control = new Control({
    element: template.content.firstChild
  });
  return control;
}

/**
 * Bound event to control template element
 */
export function addPolylineControlEvent() {
  document.addEventListener(EVENT_UPDATE_TOTAL_DISTANCE, evt => {
    let distanceElement = document.getElementById('measure-route-total-distance');
    distanceElement.innerText = formatDistance(evt.detail);
  })
}

/**
 * Set lock Button clickability and image while activate
 * Prevent lock button activation while drawing
 */
export function setlockButton() {

  let lockButton = document.getElementById('measure-route-control-control');
  let lockButtonListener = () => {

    let buttonImage = lockButton.getElementsByTagName('i')[0];

    if (buttonImage.classList.contains('icon-lock-open-alt')) {
      buttonImage.classList.remove('icon-lock-open-alt');
      buttonImage.classList.add('icon-lock');
      const event = new CustomEvent(EVENT_DISABLE_DRAWING, { detail: { state: true } });
      document.dispatchEvent(event);
    } else {
      buttonImage.classList.remove('icon-lock');
      buttonImage.classList.add('icon-lock-open-alt');
      const event = new CustomEvent(EVENT_DISABLE_DRAWING, { detail: { state: false } });
      document.dispatchEvent(event);
    }
  };

  document.addEventListener(EVENT_DRAW_STATUS, ({detail}) => {
    let lockButton = document.getElementById('measure-route-control-control');
    if (!detail.state){
      lockButton.addEventListener('click', lockButtonListener);
    } else {
      lockButton.removeEventListener('click', lockButtonListener);
    }
  });
}

/**
 * Set whether this tooltip is a transient one or not
 * @param {Overlay} tooltip
 * @param {boolean} transient
 */
export function setTooltipStyle(tooltip, transient = false) {
  tooltip.getElement().className = transient ? 'tooltip tooltip-measure' : 'tooltip tooltip-static';
}

/**
 *
 * @param {Array<Array<number>>} coordinates
 * @return {{distance: number, tooltip: Overlay}}
 */
export function addStepMeasureTooltip(coordinates, transient = false) {

  const line = new LineString(coordinates, GeometryLayout.XY);

  const coordinatesPoint1 = coordinates[coordinates.length - 1];
  const coordinatesPoint2 = coordinates[coordinates.length - 2];

  // create a new tooltip
  let tooltip = createNewTooltip(transient);
  let tooltipPosition = center(coordinatesPoint1, coordinatesPoint2);
  let {text, distance} = getLineGeometryParameters(line);
  tooltip.getElement().innerHTML = text;
  tooltip.setPosition(tooltipPosition);

  return {tooltip, distance};
}
